import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import uniqBy from "lodash/uniqBy";

import { GRID_FILTER_PARAMS } from "../../consts";
import { FilterParams, OperatorType, QueryParams, SortParams } from "../../RVGridTypes";
import { getAdditionalFiltersFromQueryParams } from "../gridFilterHelpers";

import type { FilterValue, SorterResult, SortOrder, TablePaginationConfig } from "antd/es/table/interface";

export const emptyFilterOption: { text: string; value: string } = {
    text: "None",
    value: "null",
};

//------------------------------------------------------
// Table Data and Filter Utilities
//------------------------------------------------------

/**
 * Retrieves filter value for a specific key from query parameters
 * @param queryParams - The query parameters object
 * @param key - The key to find filter value for
 * @returns The filter value as an array
 */
export function getFilterValue(queryParams: Partial<QueryParams>, key: string): FilterValue {
    const value = queryParams.filterBy?.find((filterParam) => filterParam.key === key)?.value;
    if (!value) {
        return [];
    } else if (Array.isArray(value)) {
        return value.filter((v): v is boolean | string | number => v !== null);
    }
    return [value];
}

/**
 * Retrieves sort order for a specific key from query parameters
 * @param queryParams - The query parameters object
 * @param key - The key to find sort order for
 * @param defaultSortOrder - Default sort order to use if not found
 * @returns The sort order value
 */
export function getSortOrder(queryParams: Partial<QueryParams>, key: string, defaultSortOrder: SortOrder): SortOrder {
    const value = queryParams.orderBy?.find((sortParam) => sortParam.key === key)?.value;
    if (!value) {
        return defaultSortOrder;
    }

    return value === "DESC" ? "descend" : "ascend";
}

/**
 * Maps data to column structure for table display
 * @param columns - Table column definitions
 * @param data - Raw data array
 * @returns Data formatted for table display
 */
export function mapDataToColumns(columns: any[], data: any[]): any[] {
    const keys = columns.reduce(
        (acc: string[], curr) => {
            acc.push(curr.dataIndex);
            return acc;
        },
        ["id"]
    );

    return data.reduce((acc: any[], curr) => {
        const row = {
            key: (acc.length + 1).toString(),
            ...pick(curr, keys),
        };

        acc.push(row);
        return acc;
    }, []);
}

/**
 * Helper function to process filter parameters
 * @param tableFilter - Filter value from table
 * @param queryParams - Current query parameters
 * @param key - Key for the filter
 * @param filterParams - Accumulated filter parameters
 * @param filterOperators - Operators for each filter key
 * @param columns - Table column definitions
 * @returns Updated filter parameters
 */
function getFilterBy(
    tableFilter: FilterValue | null,
    queryParams: Partial<QueryParams>,
    key: string,
    filterParams: Array<FilterParams>,
    filterOperators: Record<string, OperatorType>,
    columns: Array<any>
): Array<FilterParams> {
    const filterCandidate = find(queryParams.filterBy, { key })?.value;

    if (tableFilter && !isEmpty(tableFilter)) {
        filterParams.push({
            key,
            value: tableFilter,
            operator: filterOperators[key],
        });
        return filterParams;
    } else if (tableFilter?.length === 0 && filterCandidate && find(columns, { key })?.generateFilters) {
        // NOTE: When a new tab is opened and the column has: generateFilters() && defaultFilteredValue && filters: [].
        // The filter is not captured correctly. We can read from queryParams in that case.
        filterParams.push({
            key,
            value: filterCandidate,
            operator: filterOperators[key],
        });
        return filterParams;
    }
    return filterParams;
}

/**
 * Handles table changes (sorting, filtering, pagination) and updates query parameters
 * @param options - Configuration options for handling table changes
 * @returns A function that processes table changes
 */
export function handleTableChange({
    action,
    uiQueryParams,
    columns,
    additionalFilters = [],
    filterOperators,
}: {
    action: (value: any) => void;
    uiQueryParams: Partial<QueryParams>;
    columns: any[];
    additionalFilters: any[];
    filterOperators: Record<string, OperatorType>;
}) {
    return (
        pagination: TablePaginationConfig,
        filter: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[]
    ): void => {
        sorter = sorter as SorterResult<any>;
        let orderBy: SortParams[] | null;
        let paging: { page: number; pageSize: number };
        let filterBy: FilterParams[] = [];

        if (pagination) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            paging = { page: pagination.current!, pageSize: pagination.pageSize! };
        }

        if (filter && !isEmpty(filter)) {
            filterBy = Object.keys(filter).reduce((acc, key) => {
                return getFilterBy(filter[key], uiQueryParams, key, acc, filterOperators, columns);
            }, [] as FilterParams[]);
        }

        if (sorter.order) {
            const sortOrder = sorter.order.toString() == "ascend" ? "ASC" : "DESC";
            orderBy = [{ key: sorter.columnKey as string, value: sortOrder as string }];
        } else {
            orderBy = null;
        }

        action((prevParams: Partial<QueryParams>) => ({
            ...prevParams,
            orderBy,
            filterBy: getAdditionalFiltersFromQueryParams(prevParams.filterBy, additionalFilters).concat(filterBy),
            ...paging,
        }));
    };
}

/**
 * Generates filter options data for dropdown filters
 * @param items - Source items to generate filter options from
 * @param transformItem - Function to transform each item into a filter option
 * @param addEmptyOption - Whether to add an empty option at the beginning
 * @returns Array of filter options
 */
export function generateFilterData(
    items: any[],
    transformItem: (item: any) => { text: string; value: string },
    addEmptyOption = true
): Array<{ text: string; value: string }> {
    const filters: Array<{ text: string; value: string }> = (items || []).reduce(
        (acc, curr) => {
            acc.push(transformItem(curr));
            return acc;
        },
        [...(addEmptyOption ? [emptyFilterOption] : [])]
    );
    return uniqBy(filters, ({ value }) => value).sort((filterA, filterB) => {
        if (`${filterA.value}` === "null") {
            return -1;
        }

        if (`${filterB.value}` === "null") {
            return 1;
        }

        if (!isNaN(filterA.text as any) && !isNaN(filterB.text as any)) {
            return parseInt(filterA.text) - parseInt(filterB.text);
        }

        return filterA.text.toLowerCase() > filterB.text.toLowerCase() ? 1 : -1;
    });
}

/**
 * Calculates pagination "has more" value based on data and query params
 * @param data - Current data array
 * @param queryParams - Current query parameters
 * @param hasMore - Optional flag indicating if there's more data
 * @returns Value for pagination display
 */
export function hasMorePagination<T>(data: Array<T>, queryParams: Partial<QueryParams>, hasMore?: boolean) {
    if (data && queryParams?.page !== undefined && queryParams.pageSize) {
        // if statement should yield true when page:0, thus typeof number checking.
        if (hasMore) {
            return (queryParams.page + 1) * queryParams.pageSize + 1;
        }
        return queryParams.page * queryParams.pageSize + data.length;
    }
    return 0;
}

//------------------------------------------------------
// URL Query Parameter Utilities
//------------------------------------------------------

/**
 * Removes grid-related parameters from a URLSearchParams object
 * @param searchParams - The URLSearchParams object to clean
 * @returns A new URLSearchParams object without grid params
 */
export function removeGridQueryParams(searchParams: URLSearchParams): URLSearchParams {
    // Create a new URLSearchParams object to avoid mutating the original
    const cleanParams = new URLSearchParams();

    // Copy all non-grid parameters to the new object
    for (const [key, value] of searchParams.entries()) {
        // Skip any parameter that's part of GRID_FILTER_PARAMS
        // or starts with one of them (for array notation like orderBy[0])
        if (!GRID_FILTER_PARAMS.some((param) => key === param || key.startsWith(`${param}[`))) {
            cleanParams.append(key, value);
        }
    }

    return cleanParams;
}

/**
 * Enhanced helper to clean grid params and ensure important params are preserved
 * @param searchParams - Original URLSearchParams
 * @param preserveParams - Object with parameters to explicitly preserve
 * @param additionalParams - Object with additional parameters to add/update
 * @returns A new URLSearchParams object with clean parameters
 */
export function cleanAndUpdateURLParams(
    searchParams: URLSearchParams,
    preserveParams: { [key: string]: string | null | undefined }[] = [],
    additionalParams: { [key: string]: string | null | undefined } = {}
): URLSearchParams {
    // First remove grid params
    const cleanParams = removeGridQueryParams(searchParams);

    // Then preserve specific params from the original search params
    if (preserveParams.length > 0) {
        preserveParams.forEach((paramObj) => {
            Object.entries(paramObj).forEach(([_key, paramKey]) => {
                if (paramKey) {
                    const value = searchParams.get(paramKey);
                    if (value) {
                        cleanParams.set(paramKey, value);
                    }
                }
            });
        });
    }

    // Finally, add/update with the additional params
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            cleanParams.set(key, value);
        } else if (value === null && cleanParams.has(key)) {
            cleanParams.delete(key);
        }
    });

    return cleanParams;
}

/**
 * Creates a search string from URLSearchParams
 * @param params - URLSearchParams object
 * @returns Search string with leading question mark
 */
export function buildSearchString(params: URLSearchParams): string {
    const searchString = params.toString();
    return searchString ? `?${searchString}` : "";
}
